import {
  IMultipleRowActions,
  Modal,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import MainApi from "../../../../services/mainApi.service";
import MacroFilter, { IFilterParams } from "./MacroFilter";
import { IForubusVaccineLogData } from "../../../../models/forubus-vaccine-log";
import ForubusVaccineDetailsModal from "./ForubusVaccineDetailsModal";
import { restartForubusVaccineRetries } from "../../../../services/forubusVaccine.service";

const NFE_TOTVS_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/ForubusVaccine/ForubusVaccines";

const forubusVaccineLogService = new MainApi<IForubusVaccineLogData>(
  NFE_TOTVS_ROUTE,
);

const translationPath = "page.4uBus.logs.forubusVaccine.";

const ForubusVaccine = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IForubusVaccineLogData | null>(
    null,
  );

  const today = useMemo(() => new Date(), []);

  const { setMessage } = useNotificationContext();

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    Identifier: "",
    IndividualDocument: "",
    ImmunizationIdentifier: "",
    StatusItem: "",
    CreatedStart: today,
    CreatedEnd: today,
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { sendRequest: restartForubusVaccineRetriesReq } = useFetch(
    restartForubusVaccineRetries,
  );

  const columns: GridColDef<IForubusVaccineLogData>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identificador",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "immunizationIdentifier",
      headerName:
        t(translationPath + "immunization_identifier") ||
        "Identificador da Imunização",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "individualDocument",
      headerName:
        t(translationPath + "individual_document") || "Documento Individual",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "statusItem",
      headerName: t(translationPath + "status_item") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "-";

        const status = params.value.toUpperCase();

        const getStatusConfig = (status: string) => {
          const configs: Record<
            string,
            { color: "warning" | "info" | "success" | "error"; key: string }
          > = {
            P: { color: "warning", key: "pending" },
            PENDENTE: { color: "warning", key: "pending" },
            PR: { color: "info", key: "processing" },
            PROCESSANDO: { color: "info", key: "processing" },
            F: { color: "success", key: "finished" },
            FINALIZADO: { color: "success", key: "finished" },
            E: { color: "error", key: "error" },
            ERRO: { color: "error", key: "error" },
          };

          return (
            configs[status] || { color: "warning" as const, key: "pending" }
          );
        };

        const config = getStatusConfig(status);

        return (
          <Chip
            label={t(translationPath + `status.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },

    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "integratedDate",
      headerName:
        t(translationPath + "integrated_date") || "Data de Integração",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const dateStr = params.value.toString();
        if (
          dateStr.startsWith("0001-01-01") ||
          dateStr === "0001-01-01T00:00:00"
        ) {
          return "-";
        }

        const date = new Date(params.value);
        if (isNaN(date.getTime()) || date.getFullYear() === 1) {
          return "-";
        }

        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Tentativas",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? 0;
      },
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "vaccineIdentifier",
      headerName:
        t(translationPath + "vaccine_identifier") || "Identificador da Vacina",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
  ];

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleRestartForubusVaccineRetries = useCallback(
    async (identifier: number) => {
      const { success } = await restartForubusVaccineRetriesReq({
        identifier,
      });

      if (success) {
        onRefreshHandler();
        setMessage({
          message: t(`${translationPath}resetedSuccess`),
          type: "success",
        });
      }
    },
    [onRefreshHandler, restartForubusVaccineRetriesReq, setMessage, t],
  );

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () => [
      {
        action: async (ids) => {
          const identifiers = ids
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id));

          if (identifiers.length === 0) return;

          const { success } = await restartForubusVaccineRetriesReq({
            identifierList: identifiers,
          });

          if (success) {
            onRefreshHandler();
            setMessage({
              message: t(`${translationPath}resetedSuccess`),
              type: "success",
            });
          }
        },
        icon: <ReplayIcon fontSize="small" />,
        text: t(translationPath + "resync") || "Forçar reenvio",
      },
    ],
    [onRefreshHandler, restartForubusVaccineRetriesReq, setMessage, t],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <ForubusVaccineDetailsModal
            forubusVaccineData={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>
      <Table<IForubusVaccineLogData>
        title={t(translationPath + "title")}
        columns={columns}
        service={forubusVaccineLogService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        multipleRowsActions={multipleRowsActions}
        getRowId={(row) => (row.identifier ? row.identifier : 0)}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          params.row.statusItem === "ERRO"
            ? {
                action: () => {
                  handleRestartForubusVaccineRetries(params.row.identifier);
                },
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "resync") || "Reiniciar tentativas",
              }
            : null,
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ...filterParams,
            CreatedStart: filterParams?.CreatedStart
              ? new Date(filterParams.CreatedStart.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
            CreatedEnd: filterParams?.CreatedEnd
              ? new Date(filterParams.CreatedEnd.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default ForubusVaccine;
